import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, FileText, Ban, Send, CheckCircle, Loader2, FileCheck2, Mail } from 'lucide-react';

const STATUS_COLORS = {
  Created: 'bg-blue-50 text-blue-700 border-blue-200',
  Issued: 'bg-blue-50 text-blue-700 border-blue-200',
  Sent: 'bg-amber-50 text-amber-700 border-amber-200',
  Paid: 'bg-green-50 text-green-700 border-green-200',
  Void: 'bg-red-50 text-red-500 border-red-200',
};

export default function InvoiceDetailPage() {
  const { id } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [voiding, setVoiding] = useState(false);
  const [certBanner, setCertBanner] = useState(false);
  const [certBusy, setCertBusy] = useState(false);
  const [certResult, setCertResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/invoices/${id}`);
      setInv(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api, id]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (status, reason) => {
    try {
      const { data } = await api.put(`/invoices/${id}/status`, { status, reason });
      if (data?.just_paid) setCertBanner(true);
      load();
      setVoiding(false);
    } catch (e) { alert(e?.response?.data?.detail || 'Failed'); }
  };

  const issueCertificate = async ({ skipEmail = false } = {}) => {
    setCertBusy(true);
    setCertResult(null);
    try {
      const { data } = await api.post(`/invoices/${id}/issue-completion-certificate`, { skip_email: skipEmail });
      setCertResult(data);
      if (data?.pdf_url) {
        // Auto-open the PDF in a new tab for download / preview
        window.open(data.pdf_url, '_blank');
      }
      load();
    } catch (e) {
      setCertResult({ status: 'failed', email_message: e?.response?.data?.detail || 'Failed to issue certificate' });
    }
    setCertBusy(false);
  };

  const fmtDate = (d) => {
    if (!d) return '-';
    try { const [y,m,dd] = d.slice(0,10).split('-'); return `${dd}/${m}/${y}`; } catch { return d; }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-[#D35400]" size={24} /></div>;
  if (!inv) return <div className="text-center py-20 text-slate-400">Invoice not found</div>;

  return (
    <div data-testid="invoice-detail-page" className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/invoices')} className="p-1.5 rounded-lg hover:bg-slate-100"><ArrowLeft size={18} /></button>
          <FileText size={20} className="text-[#D35400]" />
          <div>
            <h1 className="text-xl font-bold text-slate-800">{inv.invoice_number}</h1>
            <p className="text-xs text-slate-500">{inv.client_name} — {inv.property_address}</p>
          </div>
          <span className={`ml-3 text-xs font-semibold px-3 py-1 rounded-full border ${STATUS_COLORS[inv.status] || 'bg-slate-100'}`}>
            {inv.status === 'Void' && <Ban size={10} className="inline mr-1" />}{inv.status}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {inv.status !== 'Void' && (
            <select
              data-testid="invoice-status-select"
              value={inv.status === 'Issued' ? 'Created' : inv.status}
              onChange={e => updateStatus(e.target.value)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border cursor-pointer appearance-none text-center ${STATUS_COLORS[inv.status] || STATUS_COLORS.Created}`}
              style={{ minWidth: '100px' }}
            >
              <option value="Created">Created</option>
              <option value="Sent">Sent</option>
              <option value="Paid">Paid</option>
            </select>
          )}
          {inv.status !== 'Void' && (
            voiding ? (
              <div className="flex items-center gap-1">
                <button data-testid="confirm-void-btn" onClick={() => updateStatus('Void', 'Voided by user')} className="text-xs px-3 py-1.5 bg-red-500 text-white rounded-md hover:bg-red-600">Confirm Void</button>
                <button onClick={() => setVoiding(false)} className="text-xs px-3 py-1.5 border border-slate-300 rounded-md text-slate-500 hover:bg-slate-50">Cancel</button>
              </div>
            ) : (
              <button data-testid="void-btn" onClick={() => setVoiding(true)} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-red-300 text-red-500 hover:bg-red-50">
                <Ban size={13} /> Void
              </button>
            )
          )}
          <button data-testid="view-invoice-pdf" onClick={() => navigate(`/pdf-viewer/invoice/${id}`)} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-[#D35400] text-white hover:bg-[#A04000]">
            <FileText size={13} /> View PDF
          </button>
        </div>
      </div>

      {/* Certificate of Completion banner — shows when invoice flips to Paid */}
      {(inv.status === 'Paid' && (certBanner || !inv.cert_issued_at)) && (
        <div data-testid="cert-banner" className="rounded-lg border border-emerald-200 bg-gradient-to-r from-emerald-50 to-white p-4 flex items-center gap-4 shadow-sm">
          <div className="w-10 h-10 rounded-md bg-emerald-100 text-[#0F4F1B] flex items-center justify-center flex-shrink-0">
            <FileCheck2 size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-slate-900">
              Invoice paid · Issue the Certificate of Completion
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {inv.client_name ? `Send ${inv.client_name} their 5-year workmanship warranty + yearly maintenance promise.` : 'Hand the warranty to your client now that payment has cleared.'}
              {inv.client_email ? <> Will email to <span className="font-mono text-slate-700">{inv.client_email}</span>.</> : <span className="text-amber-700"> No email on file — download only.</span>}
            </p>
            {certResult && (
              <div data-testid="cert-result" className={`mt-2 text-xs ${certResult.status === 'issued' ? 'text-emerald-700' : 'text-amber-700'}`}>
                {certResult.status === 'issued'
                  ? `✓ Certificate issued${certResult.email_status === 'sent' ? ' & emailed' : certResult.email_status === 'logged' ? ' — email logged (SMTP not configured, will retry)' : ''}.`
                  : `⚠ ${certResult.email_message || 'Failed'}`}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              data-testid="cert-issue-email-btn"
              onClick={() => issueCertificate({ skipEmail: false })}
              disabled={certBusy}
              className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-md bg-[#0F4F1B] text-white hover:bg-[#0a3812] transition-colors disabled:opacity-50"
            >
              {certBusy ? <Loader2 className="animate-spin" size={13} /> : <Mail size={13} />}
              {certBusy ? 'Issuing…' : (inv.client_email ? 'Issue + Email' : 'Issue (no email)')}
            </button>
            <button
              data-testid="cert-download-only-btn"
              onClick={() => issueCertificate({ skipEmail: true })}
              disabled={certBusy}
              className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border border-[#0F4F1B] text-[#0F4F1B] hover:bg-emerald-50 transition-colors disabled:opacity-50"
            >
              <FileText size={13} /> Download only
            </button>
            <button
              data-testid="cert-banner-dismiss"
              onClick={() => setCertBanner(false)}
              className="text-slate-400 hover:text-slate-600 text-xs"
              title="Dismiss"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border border-[#E2E2DF] rounded-lg p-4">
          <span className="text-xs text-slate-400 uppercase tracking-wider font-bold">Invoice Date</span>
          <p className="text-lg font-bold text-slate-800 mt-1">{fmtDate(inv.invoice_date)}</p>
        </div>
        <div className="bg-white border border-[#E2E2DF] rounded-lg p-4">
          <span className="text-xs text-slate-400 uppercase tracking-wider font-bold">Subtotal</span>
          <p className="text-lg font-bold text-slate-800 mt-1">${inv.subtotal?.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
        </div>
        <div className="bg-white border border-[#E2E2DF] rounded-lg p-4">
          <span className="text-xs text-slate-400 uppercase tracking-wider font-bold">GST ({inv.gst_rate}%)</span>
          <p className="text-lg font-bold text-slate-800 mt-1">${inv.gst_amount?.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
        </div>
        <div className="bg-white border border-[#E2E2DF] rounded-lg p-4">
          <span className="text-xs text-slate-400 uppercase tracking-wider font-bold">Total (inc. GST)</span>
          <p className="text-lg font-bold text-[#D35400] mt-1">${inv.total_inc_gst?.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
        </div>
      </div>

      {/* Line Items */}
      <div className="bg-white border border-[#E2E2DF] rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-[#E2E2DF]">
            <tr>
              <th className="text-left px-4 py-2.5 text-xs font-bold uppercase text-slate-500">Ref</th>
              <th className="text-left px-4 py-2.5 text-xs font-bold uppercase text-slate-500">Item</th>
              <th className="text-left px-4 py-2.5 text-xs font-bold uppercase text-slate-500">Description</th>
              <th className="text-right px-4 py-2.5 text-xs font-bold uppercase text-slate-500">Qty</th>
              <th className="text-right px-4 py-2.5 text-xs font-bold uppercase text-slate-500">Materials</th>
              <th className="text-right px-4 py-2.5 text-xs font-bold uppercase text-slate-500">Labour</th>
              <th className="text-right px-4 py-2.5 text-xs font-bold uppercase text-slate-500">Total</th>
            </tr>
          </thead>
          <tbody>
            {(inv.line_items || []).map((li, i) => (
              <tr key={i} className="border-t border-[#E2E2DF]">
                <td className="px-4 py-2 text-slate-600">{li.ref}</td>
                <td className="px-4 py-2 text-slate-800 font-medium">{li.item}</td>
                <td className="px-4 py-2 text-slate-600">{li.description}</td>
                <td className="px-4 py-2 text-right text-slate-600">{li.quantity}</td>
                <td className="px-4 py-2 text-right font-mono-num">${li.materials?.toFixed(2)}</td>
                <td className="px-4 py-2 text-right font-mono-num">${li.labour?.toFixed(2)}</td>
                <td className="px-4 py-2 text-right font-mono-num font-semibold">${li.total?.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Payments received (ledger — includes runner-recorded payments) */}
      <div className="bg-white border border-[#E2E2DF] rounded-lg p-4 mb-4" data-testid="invoice-payments">
        <h3 className="text-sm font-bold text-slate-700 mb-3">Payments received · <span data-testid="invoice-paid-total">${Number(inv.paid_total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></h3>
        {(inv.payments || []).length === 0 ? (
          <div className="text-xs text-slate-400">No payments recorded yet.</div>
        ) : (inv.payments || []).map((p, i) => (
          <div key={i} className="flex items-center gap-3 text-xs text-slate-600" data-testid={`invoice-payment-row-${i}`}>
            <span className="font-mono font-semibold">${Number(p.amount || 0).toFixed(2)}</span>
            <span>{fmtDate(p.paid_date)}</span>
            <span className="text-slate-400">{[p.reference, p.method].filter(Boolean).join(' · ')}</span>
            {p.source === 'workflow_runner' && <span className="text-[10px] px-1.5 rounded bg-sky-50 text-sky-700">runner</span>}
          </div>
        ))}
      </div>

      {/* Status History */}
      <div className="bg-white border border-[#E2E2DF] rounded-lg p-4">
        <h3 className="text-sm font-bold text-slate-700 mb-3">Status History</h3>
        <div className="space-y-2">
          {(inv.status_history || []).map((h, i) => (
            <div key={i} className="flex items-center gap-3 text-xs text-slate-500">
              <span className={`px-2 py-0.5 rounded-full font-semibold ${STATUS_COLORS[h.status] || 'bg-slate-100'}`}>{h.status}</span>
              <span>{fmtDate(h.date?.slice(0,10))}</span>
              <span className="text-slate-400">{h.by}</span>
              {h.reason && <span className="text-red-500">— {h.reason}</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
